
import { Video, TranscriptSegment } from '@/types';
import { API_CONFIG, getAuthHeaders, handleApiError } from '@/config/api';

// Types for real API responses
interface InitiateUploadResponse {
  video_id: string;
  upload_url: string;
}

interface VideoListResponse {
  videos: RealVideo[];
  total: number;
  page: number;
  page_size: number;
}

interface RealVideo {
  id: string;
  title: string;
  description?: string;
  duration?: number;
  thumbnail_url?: string;
  playback_url: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
  metadata?: {
    file_size?: number;
    resolution?: string;
    fps?: number;
  };
}

interface VideoDetailResponse {
  id: string;
  title: string;
  description?: string;
  duration: number;
  thumbnail_url?: string;
  playback_url: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
  metadata?: any;
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Real API functions
export const realVideoApi = {
  // 3-step upload process
  uploadVideo: async (file: File, token: string, title?: string, description?: string): Promise<{ id: string; status: string }> => {
    try {
      console.log('Starting 3-step upload process...');
      
      // Step 1: Initiate upload
      console.log('Step 1: Initiating upload...');
      const initiateResponse = await fetch(`${API_CONFIG.BASE_URL}/upload/initiate`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          title: title || file.name,
          description: description || '',
          file_name: file.name,
          file_size: file.size
        })
      });
      
      await handleApiError(initiateResponse);
      const { video_id, upload_url }: InitiateUploadResponse = await initiateResponse.json();
      console.log('Upload initiated, video_id:', video_id);
      
      // Step 2: Upload directly to GCS
      console.log('Step 2: Uploading to GCS...');
      const uploadResponse = await fetch(upload_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'video/mp4'
        }
      });
      
      await handleApiError(uploadResponse);
      console.log('File uploaded to GCS successfully');
      
      // Step 3: Confirm upload completion
      console.log('Step 3: Confirming upload completion...');
      const completeResponse = await fetch(`${API_CONFIG.BASE_URL}/upload/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          video_id: video_id,
          metadata: {
            title: title || file.name,
            description: description || '',
            file_name: file.name,
            file_size: file.size
          },
          processing_options: {
            generate_transcript: true,
            detect_speakers: true,
            extract_objects: true
          }
        })
      });
      
      await handleApiError(completeResponse);
      console.log('Upload process completed successfully');
      
      return { 
        id: video_id, 
        status: 'pending' 
      };
    } catch (error) {
      console.error('Upload failed:', error);
      throw error;
    }
  },
  
  // Get videos with pagination
  getVideos: async (token: string, page: number = 1, pageSize: number = API_CONFIG.DEFAULT_PAGE_SIZE): Promise<Video[]> => {
    try {
      console.log(`Fetching videos: page ${page}, size ${pageSize}`);
      const response = await fetch(`${API_CONFIG.BASE_URL}/videos/?page=${page}&page_size=${pageSize}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      await handleApiError(response);
      const data = await response.json();
      
      // Handle both array format and paginated format
      const videos = Array.isArray(data) ? data : (data.videos || []);
      
      // Convert real API format to our Video interface
      return videos.map((realVideo: RealVideo): Video => ({
        id: realVideo.id,
        title: realVideo.title,
        duration: realVideo.duration || 0,
        thumbnail: realVideo.thumbnail_url || 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=500&h=280&fit=crop',
        processingStatus: realVideo.status,
        createdAt: realVideo.created_at,
        playbackUrl: realVideo.playback_url, // Add this for real videos
        metadata: {
          speakers: [], // Will be populated when we have transcript data
          objects: [],
          frameCount: 0,
          segmentCount: 0
        }
      }));
    } catch (error) {
      console.error('Failed to fetch videos:', error);
      throw error;
    }
  },
  
  // Get specific video details with playback URL
  getVideoById: async (id: string, token: string): Promise<Video | undefined> => {
    try {
      console.log(`Fetching video details for ID: ${id}`);
      const response = await fetch(`${API_CONFIG.BASE_URL}/videos/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      await handleApiError(response);
      const realVideo: VideoDetailResponse = await response.json();
      
      // Convert to our Video interface
      return {
        id: realVideo.id,
        title: realVideo.title,
        duration: realVideo.duration,
        thumbnail: realVideo.thumbnail_url || 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=500&h=280&fit=crop',
        processingStatus: realVideo.status,
        createdAt: realVideo.created_at,
        playbackUrl: realVideo.playback_url,
        metadata: {
          speakers: [],
          objects: [],
          frameCount: 0,
          segmentCount: 0
        }
      };
    } catch (error) {
      console.error('Failed to fetch video details:', error);
      return undefined;
    }
  }
};
